import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { api } from 'common';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import { C2, C2_FONT, C2TableCard, C2SectionHeading } from '../components/Table/C2UserTable';
import { USERTYPE_LABELS, DEVICETYPE_LABELS, pushCoverage } from '../components/notifications/pushReport';
import logo from '../assets/img/logo192x192.png';

const TITLE_MAX = 100;
const BODY_MAX = 500;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
  '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
};

const pillSx = (active) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  border: `1px solid ${active ? C2.violet : C2.border}`,
  color: active ? '#FFFFFF' : C2.dark,
  backgroundColor: active ? C2.violet : '#FFFFFF',
  '&:hover': { backgroundColor: active ? C2.violet : C2.lavender },
});

function Label({ children }) {
  return <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mb: 1 }}>{children}</Typography>;
}

function PhonePreview({ platform, title, body }) {
  const ios = platform === 'IOS';
  const Icon = ios ? AppleIcon : AndroidIcon;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <Icon sx={{ fontSize: 16, color: ios ? C2.sidebar : '#15803D' }} />
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', fontWeight: 700, color: C2.muted }}>{ios ? 'iPhone' : 'Android'}</Typography>
      </Box>
      <Box
        sx={{
          p: 1.25,
          borderRadius: ios ? '18px' : '14px',
          background: ios ? 'rgba(251,250,254,0.92)' : '#FFFFFF',
          boxShadow: '0 8px 20px rgba(26,15,59,0.18)',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <Box component="img" src={logo} alt="" sx={{ width: ios ? 34 : 22, height: ios ? 34 : 22, borderRadius: ios ? '8px' : '50%', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: ios ? '0.8rem' : '0.72rem', fontWeight: ios ? 700 : 400, color: ios ? '#111' : C2.muted }}>
              {ios ? title || 'Título de la notificación' : 'Conecta2 · ahora'}
            </Typography>
            {ios ? <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.7rem', color: '#6B7280' }}>ahora</Typography> : null}
          </Box>
          {!ios ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', fontWeight: 700, color: '#111', wordBreak: 'break-word' }}>{title || 'Título de la notificación'}</Typography>
          ) : null}
          <Typography
            sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: '#374151', display: '-webkit-box', WebkitLineClamp: ios ? 4 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}
          >
            {body || 'Aquí verás el mensaje tal como aparece en el celular.'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function AddNotifications() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const settings = useSelector((s) => s.settingsdata.settings);
  const users = useSelector((s) => s.usersdata.users);
  const zonificacion = useSelector((s) => s.zonificacion);
  const template = state?.template || {};

  useEffect(() => {
    if (typeof api.fetchZonificacionCities === 'function') {
      dispatch(api.fetchZonificacionCities());
    }
  }, [dispatch]);

  const activeCities = useMemo(() => {
    return (zonificacion?.cities || [])
      .filter((c) => c && c.active)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
  }, [zonificacion?.cities]);

  const [title, setTitle] = useState(String(template.title || '').slice(0, TITLE_MAX));
  const [body, setBody] = useState(String(template.body || '').slice(0, BODY_MAX));
  const [usertype, setUsertype] = useState(USERTYPE_LABELS[template.usertype] ? template.usertype : 'driver');
  const [devicetype, setDevicetype] = useState(DEVICETYPE_LABELS[template.devicetype] ? template.devicetype : 'All');
  const [cityId, setCityId] = useState(template.cityId || 'All');
  const [touched, setTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selectedCity = useMemo(() => {
    if (!cityId || cityId === 'All') return null;
    return activeCities.find((c) => c.id === cityId) || null;
  }, [cityId, activeCities]);

  const coverage = useMemo(
    () => pushCoverage(users, usertype, devicetype, cityId, selectedCity?.name),
    [users, usertype, devicetype, cityId, selectedCity]
  );
  const titleError = touched && !title.trim() ? 'Escribe un título.' : '';
  const bodyError = touched && !body.trim() ? 'Escribe el mensaje.' : '';

  const requestSend = () => {
    setTouched(true);
    setError('');
    if (!title.trim() || !body.trim()) return;
    if (!settings?.AllowCriticalEditsAdmin) {
      setError('El panel está en modo demo: el envío de notificaciones está desactivado.');
      return;
    }
    setConfirmOpen(true);
  };

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const result = await dispatch(
        api.sendNotification({
          title: title.trim(),
          body: body.trim(),
          usertype,
          devicetype,
          cityId: cityId || 'All',
          cityName: selectedCity ? selectedCity.name : 'Todas las ciudades',
          createdAt: new Date().getTime(),
        })
      );
      const a = result.audience || {};
      let msg;
      let severity = 'success';
      if (result.warning === 'no_devices') {
        severity = 'warning';
        msg = `Ninguna de las ${a.users || 0} cuentas seleccionadas tiene notificaciones activas. El mensaje quedó en la bandeja de la app.`;
      } else if (result.queued) {
        msg = `Envío en curso a ${a.devices} dispositivos (${a.ANDROID || 0} Android · ${a.IOS || 0} iOS). El avance se actualiza en el historial.`;
      } else {
        const rejected = Object.values(result.tickets || {}).reduce((acc, p) => acc + (Number(p?.error) || 0), 0);
        msg = `Notificación enviada a ${a.devices} dispositivos (${a.ANDROID || 0} Android · ${a.IOS || 0} iOS).${rejected ? ` ${rejected} fueron rechazados; revisa el detalle.` : ''} La entrega se confirmará en unos minutos.`;
        if (rejected) severity = 'warning';
      }
      navigate('/notifications', { state: { sent: { severity, msg } } });
    } catch (e) {
      setConfirmOpen(false);
      setError(e?.message || 'No se pudo enviar la notificación. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <Box sx={{ mb: 2 }}>
        <C2SecondaryButton startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/notifications')}>
          Volver al historial
        </C2SecondaryButton>
      </Box>

      {error ? (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <C2TableCard sx={{ p: { xs: 2, sm: 3 } }}>
            <C2SectionHeading title="Mensaje" subtitle="Corto y claro: en Android se ven unas dos líneas antes de expandir." />
            <TextField
              fullWidth
              label="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              error={!!titleError}
              helperText={titleError || `${title.length}/${TITLE_MAX}`}
              sx={{ ...fieldSx, mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Mensaje"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              error={!!bodyError}
              helperText={bodyError || `${body.length}/${BODY_MAX}`}
              sx={{ ...fieldSx, mb: 2.5 }}
            />

            <Label>¿A quién va dirigida?</Label>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
              {['driver', 'customer', 'All'].map((k) => (
                <Chip key={k} label={USERTYPE_LABELS[k]} onClick={() => setUsertype(k)} sx={pillSx(usertype === k)} />
              ))}
            </Box>

            <Label>Dispositivos</Label>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {['All', 'ANDROID', 'IOS'].map((k) => (
                <Chip key={k} label={DEVICETYPE_LABELS[k]} onClick={() => setDevicetype(k)} sx={pillSx(devicetype === k)} />
              ))}
            </Box>
            {devicetype !== 'All' && coverage.unknownPlatform ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: '#B45309', mb: 2 }}>
                {coverage.unknownPlatform} cuentas no registran su plataforma y quedarán fuera. Elige "Todos los dispositivos" para incluirlas.
              </Typography>
            ) : null}

            <Label>Ciudad activa / Zona</Label>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip
                label="Todas las ciudades"
                onClick={() => setCityId('All')}
                sx={pillSx(cityId === 'All')}
              />
              {activeCities.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  onClick={() => setCityId(c.id)}
                  sx={pillSx(cityId === c.id)}
                />
              ))}
            </Box>
            {selectedCity ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.violet, fontWeight: 600, mb: 1 }}>
                📍 Notificación segmentada solo para la zona activa de {selectedCity.name}.
              </Typography>
            ) : null}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <C2PrimaryButton startIcon={<SendRoundedIcon />} onClick={requestSend} disabled={sending}>
                Enviar notificación
              </C2PrimaryButton>
            </Box>
          </C2TableCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <C2TableCard sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <C2SectionHeading
              title="Alcance estimado"
              subtitle={
                selectedCity
                  ? `Cuentas registradas en la ciudad activa de ${selectedCity.name}.`
                  : 'Según las cuentas con notificaciones activas en este momento.'
              }
            />
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '2rem', color: C2.violet, lineHeight: 1 }}>{coverage.devices}</Typography>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: C2.muted }}>dispositivos de {coverage.users} cuentas</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AndroidIcon sx={{ fontSize: 18, color: '#15803D' }} />
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>{coverage.ANDROID} Android</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AppleIcon sx={{ fontSize: 18, color: C2.sidebar }} />
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>{coverage.IOS} iOS</Typography>
              </Box>
              {coverage.UNKNOWN ? (
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: C2.muted }}>{coverage.UNKNOWN} sin plataforma</Typography>
              ) : null}
            </Box>
            {coverage.withoutDevice ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: '#B45309' }}>
                {coverage.withoutDevice} cuentas no tienen notificaciones activas: verán el mensaje solo en la bandeja de la app.
              </Typography>
            ) : null}
          </C2TableCard>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: '16px',
              background: `linear-gradient(160deg, ${C2.sidebar} 0%, #2C195B 55%, ${C2.violet} 140%)`,
              boxShadow: '0 12px 28px rgba(26,15,59,0.25)',
            }}
          >
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#FFFFFF', mb: 1.5 }}>VISTA PREVIA</Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {devicetype !== 'IOS' ? <PhonePreview platform="ANDROID" title={title} body={body} /> : null}
              {devicetype !== 'ANDROID' ? <PhonePreview platform="IOS" title={title} body={body} /> : null}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <C2Dialog
        open={confirmOpen}
        onClose={() => (sending ? null : setConfirmOpen(false))}
        title="Confirmar envío"
        icon={NotificationsActiveOutlinedIcon}
        actions={
          <>
            <C2SecondaryButton onClick={() => setConfirmOpen(false)} disabled={sending}>
              Cancelar
            </C2SecondaryButton>
            <C2PrimaryButton onClick={send} disabled={sending}>
              {sending ? 'Enviando…' : 'Enviar ahora'}
            </C2PrimaryButton>
          </>
        }
      >
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.9rem', color: C2.sidebar }}>
          "{title.trim()}" se enviará a <b>{USERTYPE_LABELS[usertype].toLowerCase()}</b> ({DEVICETYPE_LABELS[devicetype].toLowerCase()})
          {selectedCity ? (
            <> en la ciudad activa de <b>{selectedCity.name}</b></>
          ) : (
            <> en todas las ciudades</>
          )}
          : unos <b>{coverage.devices}</b> dispositivos. No se puede deshacer.
        </Typography>
      </C2Dialog>
    </Box>
  );
}
